import { ExportMenu } from '@/components/shared/export-menu';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { EmptyState, ErrorState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { money } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useByBarber, useByMethod, useDaily, useSummary, useTopServices, type ReportFilters } from '../api';
import { KIND_LABEL } from '../labels';
import { DailyChart } from './daily-chart';

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-2">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function Indicator({ label, value, legend, tone, big }: { label: string; value: number; legend: string; tone?: 'bad'; big?: boolean }) {
  return (
    <div className="grid content-start gap-0.5 bg-card p-4">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className={cn('tabular font-semibold', big ? 'font-display text-4xl leading-none' : 'text-xl', tone === 'bad' && 'text-oxblood-ink')}>{money(value)}</dd>
      <dd className="text-xs text-muted-foreground">{legend}</dd>
    </div>
  );
}

function Summary({ f }: { f: ReportFilters }) {
  const q = useSummary(f);
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading || !q.data) return <Skeleton className="h-52 w-full" />;
  const s = q.data;
  const result = s.receivedCents - s.expensesCents;
  return (
    <dl className="grid gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-2 lg:grid-cols-4">
      <Indicator big label="Faturamento" value={s.revenueCents} legend={`Soma dos ${s.completedCount} atendimentos concluídos, já com descontos.`} />
      <Indicator big label="Recebido" value={s.receivedCents} legend="Pagamentos confirmados no caixa, menos estornos." />
      <Indicator big label="Despesas" value={s.expensesCents} legend="Despesas pagas no período." />
      <Indicator big label="Resultado" value={result} tone={result < 0 ? 'bad' : undefined} legend="Recebido menos despesas." />
      <Indicator label="Pendente de receber" value={s.pendingCents} legend="Concluído e ainda sem pagamento integral." />
      <Indicator label="Ticket médio" value={s.averageTicketCents} legend="Faturamento dividido pelos atendimentos concluídos." />
      <Indicator label="Comissões" value={s.commissionsCents} legend="Comissões geradas sobre o valor recebido." />
    </dl>
  );
}

function Daily({ f }: { f: ReportFilters }) {
  const q = useDaily(f);
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading) return <Skeleton className="h-72 w-full" />;
  const hasData = (q.data ?? []).some((d) => d.revenueCents > 0 || d.receivedCents > 0);
  if (!hasData) return <EmptyState title="Sem movimento no período" description="Conclua e receba atendimentos para ver a evolução diária." />;
  return (
    <div className="rounded-lg border bg-card p-4">
      <DailyChart data={q.data ?? []} />
    </div>
  );
}

function SimpleTable({ head, rows, empty }: { head: { label: string; right?: boolean }[]; rows: React.ReactNode[][]; empty: string }) {
  if (rows.length === 0) return <EmptyState title={empty} />;
  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {head.map((h) => (
              <TableHead key={h.label} className={cn('text-xs font-semibold text-muted-foreground', h.right && 'text-right')}>
                {h.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              {r.map((c, j) => (
                <TableCell key={j} className={cn(head[j]?.right && 'tabular text-right')}>
                  {c}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function Barbers({ f }: { f: ReportFilters }) {
  const q = useByBarber(f);
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading) return <Skeleton className="h-32 w-full" />;
  const rows = (q.data ?? []).map((b) => [
    <span key="n" className="inline-flex items-center gap-2">
      <span className="size-2.5 rounded-full" style={{ backgroundColor: b.color }} aria-hidden />
      {b.barberName}
    </span>,
    b.completedCount,
    money(b.revenueCents),
    money(b.averageTicketCents),
    money(b.commissionsCents),
    b.goalPercent === null || b.monthlyGoalCents === null ? (
      <span key="g" className="text-muted-foreground">Sem meta</span>
    ) : (
      <div key="g" className="ml-auto grid w-40 gap-1 text-left">
        <div role="progressbar" aria-label={`Meta de ${b.barberName}`} aria-valuenow={Math.round(b.goalPercent)} aria-valuemin={0} aria-valuemax={100} className="h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, b.goalPercent)}%` }} />
        </div>
        <span className="text-xs text-muted-foreground">
          {Math.round(b.goalPercent)}% de {money(b.monthlyGoalCents)}
        </span>
      </div>
    ),
  ]);
  return (
    <SimpleTable
      empty="Nenhum atendimento concluído no período"
      head={[{ label: 'Profissional' }, { label: 'Atendimentos', right: true }, { label: 'Faturamento', right: true }, { label: 'Ticket médio', right: true }, { label: 'Comissão', right: true }, { label: 'Meta mensal', right: true }]}
      rows={rows}
    />
  );
}

function TopServices({ f }: { f: ReportFilters }) {
  const q = useTopServices(f);
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading) return <Skeleton className="h-32 w-full" />;
  return (
    <SimpleTable
      empty="Nenhum serviço vendido no período"
      head={[{ label: 'Serviço' }, { label: 'Qtd.', right: true }, { label: 'Faturamento', right: true }]}
      rows={(q.data ?? []).map((s) => [s.serviceName, s.count, money(s.revenueCents)])}
    />
  );
}

function ByMethod({ f }: { f: ReportFilters }) {
  const q = useByMethod(f);
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading) return <Skeleton className="h-32 w-full" />;
  return (
    <SimpleTable
      empty="Nenhum recebimento no período"
      head={[{ label: 'Método' }, { label: 'Recebimentos', right: true }, { label: 'Valor', right: true }, { label: 'Taxas', right: true }]}
      rows={(q.data ?? []).map((m) => [`${m.methodName} (${KIND_LABEL[m.methodKind]})`, m.paymentsCount, money(m.amountCents), money(m.feeCents)])}
    />
  );
}

export function ExportButton({ f }: { f: ReportFilters }) {
  return (
    <ExportMenu
      options={[
        { label: 'Planilha (XLSX)', path: '/reports/export', query: f, filename: 'relatorio-financeiro.xlsx' },
        { label: 'PDF', path: '/reports/pdf', query: f, filename: 'relatorio-financeiro.pdf' },
      ]}
    />
  );
}

export function Overview({ f }: { f: ReportFilters }) {
  return (
    <div className="grid gap-8">
      <Section title="Indicadores do período">
        <Summary f={f} />
      </Section>
      <Section title="Evolução diária" hint="Faturamento conta o atendimento concluído; recebido conta o pagamento confirmado no caixa.">
        <Daily f={f} />
      </Section>
      <Section title="Desempenho por profissional">
        <Barbers f={f} />
      </Section>
      <div className="grid gap-8 lg:grid-cols-2">
        <Section title="Serviços mais vendidos">
          <TopServices f={f} />
        </Section>
        <Section title="Por método de pagamento">
          <ByMethod f={f} />
        </Section>
      </div>
    </div>
  );
}
